import type {
	FilterableListFilter,
	FilterableListItems,
} from '@valk-nx/storyblok-types/src/lib/types/filterable-list.service.interface';

export type FiltersList = Record<string, string[]>;

/** Keys of `FilterableListItems` whose value is a string array (e.g. `themeGuids`). */
export type ArrayKeys = {
	[K in keyof FilterableListItems]-?: NonNullable<
		FilterableListItems[K]
	> extends string[]
		? K
		: never;
}[keyof FilterableListItems];

const NUMERIC_FILTER_MIN_VALUE = 1;

/**
 * Drops active filters the given definitions don't support: unknown group
 * titles, values not present in the group's items, and non-canonical or
 * out-of-range numeric values. Key and value order of `active` is preserved,
 * so a fully valid map comes back structurally equal to its input.
 */
export function pruneFilters(
	active: FiltersList,
	definitions: FilterableListFilter[],
): FiltersList {
	const pruned: FiltersList = {};

	for (const [title, values] of Object.entries(active)) {
		const definition = definitions.find((def) => def.title === title);
		if (!definition) continue;

		if (definition.type === 'numeric') {
			const value = values[0];
			// Canonical integer only (no '022'), so the request path derived
			// from the value stays deterministic.
			const numericValue = /^(0|[1-9]\d*)$/.test(value)
				? Number(value)
				: Number.NaN;
			if (
				numericValue >= NUMERIC_FILTER_MIN_VALUE &&
				numericValue <= (definition.maxValue ?? Infinity)
			) {
				pruned[title] = [value];
			}
			continue;
		}

		const codes = new Set(definition.data?.map((item) => item.code));
		const validValues = values.filter((value) => codes.has(value));
		if (validValues.length) {
			pruned[title] = validValues;
		}
	}

	return pruned;
}

// Keys must match a filter group title in the API response: they are seeded
// as active filters instead of fixed request params, so an unknown key is
// silently dropped by pruneFilters.
const PRESELECTABLE_FILTER_KEYS: readonly ArrayKeys[] = ['countries'];

export function preselectedFilters(
	items: FilterableListItems | null,
): FiltersList {
	const filters: FiltersList = {};

	for (const key of PRESELECTABLE_FILTER_KEYS) {
		const values = items?.[key] ?? [];
		if (values.length) {
			filters[key] = [...values];
		}
	}

	return filters;
}
